import React, { useContext } from 'react'
import { BlueConicIntegration } from './blueconic-integration'
import {
    GetBlueConicDomainIntegrationResponse,
    GetDomainIntegrationResponse,
    isCreateBlueConicDomainIntegrationRequest,
} from '@pushly/models/lib/structs/domains'
import { BlueConicIntegrationEditor } from './blueconic-integration-editor'
import { BlueConicIntegrationRemover } from './blueconic-integration-remover'
import { BlueConicActionRequest, DomainIntegrationsManagerContext } from '../../context'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { DomainIntegrationsManagerDispatchContext } from '../../reducer'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppService, DomainIntegrationService } from '../../../../services'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { useHistory } from 'react-router-dom'

const isBlueConicProvider = (di: GetDomainIntegrationResponse) =>
    di.provider === DomainIntegrationProviders.BLUECONIC_REC

const toBlueConicGetResponse = (di: GetDomainIntegrationResponse) =>
    GetBlueConicDomainIntegrationResponse.fromGetDomainIntegrationResponse(di)

function isEditorActionRequest(req: any): req is BlueConicActionRequest<'add' | 'edit'> {
    return req?.type === 'add' || req?.type === 'edit'
}

function isRemovalActionRequest(req: any): req is BlueConicActionRequest<'rem'> {
    return req?.type === 'rem'
}

export const BlueConicIntegrationsManager = () => {
    const ctx = useContext(DomainIntegrationsManagerContext)
    const dispatch = useContext(DomainIntegrationsManagerDispatchContext)
    const diSvc = useService(DomainIntegrationService)
    const appSvc = useService(AppService)
    const history = useHistory()

    const { loading, domain, integrations } = ctx

    let openActionRequest:
        | BlueConicActionRequest<'add'>
        | BlueConicActionRequest<'edit'>
        | BlueConicActionRequest<'rem'>
        | undefined
    if (ctx.actionRequest?.provider === DomainIntegrationProviders.BLUECONIC_REC) {
        openActionRequest = ctx.actionRequest
    }

    const bcIntegrations: GetBlueConicDomainIntegrationResponse[] =
        integrations?.filter(isBlueConicProvider).map(toBlueConicGetResponse) ?? []

    return (
        <>
            <BlueConicIntegration
                loading={loading}
                mode="add"
                onActionClick={() => {
                    dispatch({ type: 'request-integration-add', provider: DomainIntegrationProviders.BLUECONIC_REC })
                }}
            />
            {bcIntegrations.map((di) => (
                <BlueConicIntegration
                    key={di.id}
                    mode="edit"
                    integration={di}
                    onActionClick={(action, selectedIntegration) => {
                        action === 'primary'
                            ? dispatch({
                                  type: 'request-integration-rem',
                                  provider: DomainIntegrationProviders.BLUECONIC_REC,
                                  integration: selectedIntegration,
                              })
                            : dispatch({
                                  type: 'request-integration-edit',
                                  provider: DomainIntegrationProviders.BLUECONIC_REC,
                                  integration: selectedIntegration,
                              })
                    }}
                />
            ))}

            <BlueConicIntegrationEditor
                mode={isEditorActionRequest(openActionRequest) ? openActionRequest.type : undefined}
                source={isEditorActionRequest(openActionRequest) ? openActionRequest.data : undefined}
                processing={isEditorActionRequest(openActionRequest) ? openActionRequest.processing : undefined}
                onSave={async (req) => {
                    if (loading) return
                    dispatch({ type: 'set-action-request-processing' })

                    if (isCreateBlueConicDomainIntegrationRequest(req)) {
                        const res = await diSvc.createInDomain(domain.id, req)
                        if (!res.ok) {
                            if (!res.cancelled) {
                                simpleNotification('error', res.error?.message ?? 'Unable to update integration.')
                            }
                            return
                        }

                        simpleNotification('success', 'Integration successfully added.')
                        dispatch({ type: 'add-integration', integration: res.data! })
                    } else {
                        // if no changes detected don't seed to API layer
                        if (!req.equals(openActionRequest?.data)) {
                            const res = await diSvc.updateById(domain.id, req)
                            if (!res.ok) {
                                if (!res.cancelled) {
                                    simpleNotification('error', res.error?.message ?? 'Unable to update integration.')
                                }
                                return
                            }

                            dispatch({ type: 'set-integration', integration: res.data! })
                        }

                        simpleNotification('success', 'Integration successfully updated.')
                    }

                    dispatch({ type: 'clear-action-request' })
                }}
                onCancel={() => dispatch({ type: 'clear-action-request' })}
            />

            <BlueConicIntegrationRemover
                source={isRemovalActionRequest(openActionRequest) ? openActionRequest.data : undefined}
                processing={isRemovalActionRequest(openActionRequest) ? openActionRequest.processing : undefined}
                onConfirm={async (req) => {
                    if (loading) return
                    dispatch({ type: 'set-action-request-processing' })

                    const res = await diSvc.removeIntegration(domain.id, req.id)
                    if (!res.ok) {
                        if (!res.cancelled) {
                            simpleNotification('error', res.error?.message ?? 'Unable to remove integration.')
                        }
                        return
                    }

                    simpleNotification('success', 'Integration successfully removed.')
                    dispatch({ type: 'rem-integration', integration: res.data! })
                    dispatch({ type: 'clear-action-request' })
                }}
                onCancel={() => dispatch({ type: 'clear-action-request' })}
            />
        </>
    )
}
